export enum SensorType {
  PM = 'pm',
  TempRh = 'temp_rh',
  Baro = 'baro',
  GasCO = 'gas_co',
  GasCO2 = 'gas_co2',
  GasO3 = 'gas_o3',
  GasVOC = 'gas_voc',
  GasSO2 = 'gas_so2',
  GasNO2 = 'gas_no2',
  GasCH2O = 'gas_ch2o'
}

const MEASUREMENT_IMG_SRC = {
  PM: '/img/measurements/pm.svg',
  TEMP: '/img/measurements/temp.svg',
  RH: '/img/measurements/rh.svg',
  BP: '/img/measurements/bp.svg',
  CO: '/img/measurements/co.svg',
  CO2: '/img/measurements/co2.svg',
  O3: '/img/measurements/o3.svg',
  VOC: '/img/measurements/voc.svg',
  SO2: '/img/measurements/so2.svg',
  NO2: '/img/measurements/no2.svg',
  CH2O: '/img/measurements/ch2o.svg',
  MINIMUM: '/img/measurements/minimum.svg',
  MAXIMUM: '/img/measurements/maximum.svg',
  AVERAGE: '/img/measurements/average.svg',
  TWA: '/img/measurements/twa.svg'
} as const

const MEASUREMENT_FRIENDLY_NAME_PATH = 'map.historyDialog.dropdown'

/**
 * Numeric enum mapping measurement names to index values.
 * Use `MeasurementType.Pm25` etc. in new code.
 */
export enum MeasurementType {
  Pm25 = 0,
  Pm10 = 1,
  Temp = 2,
  Rh = 3,
  Pm25Raw = 4,
  Pm10Raw = 5,
  BaroInhg = 6,
  Co = 7,
  Co2 = 8,
  O3 = 9,
  Tvoc = 10,
  So2 = 11,
  No2 = 12,
  Ch2o = 13,
  Pm1x0 = 14,
  Pm4x0 = 15,
  PmTotal = 16,
  Pm1x0Min = 17,
  Pm1x0Max = 18,
  Pm1x0Avg = 19,
  Pm1x0Twa = 20,
  Pm2x5Min = 21,
  Pm2x5Max = 22,
  Pm2x5Avg = 23,
  Pm2x5Twa = 24,
  Pm4x0Min = 25,
  Pm4x0Max = 26,
  Pm4x0Avg = 27,
  Pm4x0Twa = 28,
  Pm10Min = 29,
  Pm10Max = 30,
  Pm10Avg = 31,
  Pm10Twa = 32,
  PmTotalMin = 33,
  PmTotalMax = 34,
  PmTotalAvg = 35,
  PmTotalTwa = 36,
  Pm25IndiaAqi = 37,
  Pm10IndiaAqi = 38
}

export const measurementShortNameToType: Partial<Record<string, MeasurementType>> = {
  pm25: MeasurementType.Pm25,
  pm10: MeasurementType.Pm10,
  temp: MeasurementType.Temp,
  rh: MeasurementType.Rh,
  pm25_raw: MeasurementType.Pm25Raw,
  pm10_raw: MeasurementType.Pm10Raw,
  baro_inhg: MeasurementType.BaroInhg,
  co: MeasurementType.Co,
  co2: MeasurementType.Co2,
  o3: MeasurementType.O3,
  tvoc: MeasurementType.Tvoc,
  so2: MeasurementType.So2,
  no2: MeasurementType.No2,
  ch2o: MeasurementType.Ch2o,
  pm1x0: MeasurementType.Pm1x0,
  pm4x0: MeasurementType.Pm4x0,
  pm_total: MeasurementType.PmTotal,
  pm1x0_min: MeasurementType.Pm1x0Min,
  pm1x0_max: MeasurementType.Pm1x0Max,
  pm1x0_avg: MeasurementType.Pm1x0Avg,
  pm1x0_twa: MeasurementType.Pm1x0Twa,
  pm2x5_min: MeasurementType.Pm2x5Min,
  pm2x5_max: MeasurementType.Pm2x5Max,
  pm2x5_avg: MeasurementType.Pm2x5Avg,
  pm2x5_twa: MeasurementType.Pm2x5Twa,
  pm4x0_min: MeasurementType.Pm4x0Min,
  pm4x0_max: MeasurementType.Pm4x0Max,
  pm4x0_avg: MeasurementType.Pm4x0Avg,
  pm4x0_twa: MeasurementType.Pm4x0Twa,
  pm10_min: MeasurementType.Pm10Min,
  pm10_max: MeasurementType.Pm10Max,
  pm10_avg: MeasurementType.Pm10Avg,
  pm10_twa: MeasurementType.Pm10Twa,
  pm_total_min: MeasurementType.PmTotalMin,
  pm_total_max: MeasurementType.PmTotalMax,
  pm_total_avg: MeasurementType.PmTotalAvg,
  pm_total_twa: MeasurementType.PmTotalTwa,
  pm25_india_aqi: MeasurementType.Pm25IndiaAqi,
  pm10_india_aqi: MeasurementType.Pm10IndiaAqi
} as const

export const measurementTypesKey: Record<MeasurementType, string> = {
  [MeasurementType.Pm25]: 'mcpm2x5_aqi',
  [MeasurementType.Pm10]: 'mcpm10_aqi',
  [MeasurementType.Temp]: 'temp_c',
  [MeasurementType.Rh]: 'rh_percent',
  [MeasurementType.Pm25Raw]: 'mcpm2x5',
  [MeasurementType.Pm10Raw]: 'mcpm10',
  [MeasurementType.BaroInhg]: 'baro_inhg',
  [MeasurementType.Co]: 'co_ppm',
  [MeasurementType.Co2]: 'co2_ppm',
  [MeasurementType.O3]: 'o3_ppb',
  [MeasurementType.Tvoc]: 'voc_mgm3',
  [MeasurementType.So2]: 'so2_ppb',
  [MeasurementType.No2]: 'no2_ppb',
  [MeasurementType.Ch2o]: 'ch2o_ppb',
  [MeasurementType.Pm1x0]: 'mcpm1x0',
  [MeasurementType.Pm4x0]: 'mcpm4x0',
  [MeasurementType.PmTotal]: 'mcpm_total',
  [MeasurementType.Pm1x0Min]: 'mcpm1x0_min',
  [MeasurementType.Pm1x0Max]: 'mcpm1x0_max',
  [MeasurementType.Pm1x0Avg]: 'mcpm1x0_avg',
  [MeasurementType.Pm1x0Twa]: 'mcpm1x0_twa',
  [MeasurementType.Pm2x5Min]: 'mcpm2x5_min',
  [MeasurementType.Pm2x5Max]: 'mcpm2x5_max',
  [MeasurementType.Pm2x5Avg]: 'mcpm2x5_avg',
  [MeasurementType.Pm2x5Twa]: 'mcpm2x5_twa',
  [MeasurementType.Pm4x0Min]: 'mcpm4x0_min',
  [MeasurementType.Pm4x0Max]: 'mcpm4x0_max',
  [MeasurementType.Pm4x0Avg]: 'mcpm4x0_avg',
  [MeasurementType.Pm4x0Twa]: 'mcpm4x0_twa',
  [MeasurementType.Pm10Min]: 'mcpm10_min',
  [MeasurementType.Pm10Max]: 'mcpm10_max',
  [MeasurementType.Pm10Avg]: 'mcpm10_avg',
  [MeasurementType.Pm10Twa]: 'mcpm10_twa',
  [MeasurementType.PmTotalMin]: 'mcpm_total_min',
  [MeasurementType.PmTotalMax]: 'mcpm_total_max',
  [MeasurementType.PmTotalAvg]: 'mcpm_total_avg',
  [MeasurementType.PmTotalTwa]: 'mcpm_total_twa',
  [MeasurementType.Pm25IndiaAqi]: 'mcpm2x5_india_aqi',
  [MeasurementType.Pm10IndiaAqi]: 'mcpm10_india_aqi'
} as const

export const sensorTypesKey: Record<MeasurementType, SensorType> = {
  [MeasurementType.Pm25]: SensorType.PM,
  [MeasurementType.Pm10]: SensorType.PM,
  [MeasurementType.Temp]: SensorType.TempRh,
  [MeasurementType.Rh]: SensorType.TempRh,
  [MeasurementType.Pm25Raw]: SensorType.PM,
  [MeasurementType.Pm10Raw]: SensorType.PM,
  [MeasurementType.BaroInhg]: SensorType.Baro,
  [MeasurementType.Co]: SensorType.GasCO,
  [MeasurementType.Co2]: SensorType.GasCO2,
  [MeasurementType.O3]: SensorType.GasO3,
  [MeasurementType.Tvoc]: SensorType.GasVOC,
  [MeasurementType.So2]: SensorType.GasSO2,
  [MeasurementType.No2]: SensorType.GasNO2,
  [MeasurementType.Ch2o]: SensorType.GasCH2O,
  [MeasurementType.Pm1x0]: SensorType.PM,
  [MeasurementType.Pm4x0]: SensorType.PM,
  [MeasurementType.PmTotal]: SensorType.PM,
  [MeasurementType.Pm1x0Min]: SensorType.PM,
  [MeasurementType.Pm1x0Max]: SensorType.PM,
  [MeasurementType.Pm1x0Avg]: SensorType.PM,
  [MeasurementType.Pm1x0Twa]: SensorType.PM,
  [MeasurementType.Pm2x5Min]: SensorType.PM,
  [MeasurementType.Pm2x5Max]: SensorType.PM,
  [MeasurementType.Pm2x5Avg]: SensorType.PM,
  [MeasurementType.Pm2x5Twa]: SensorType.PM,
  [MeasurementType.Pm4x0Min]: SensorType.PM,
  [MeasurementType.Pm4x0Max]: SensorType.PM,
  [MeasurementType.Pm4x0Avg]: SensorType.PM,
  [MeasurementType.Pm4x0Twa]: SensorType.PM,
  [MeasurementType.Pm10Min]: SensorType.PM,
  [MeasurementType.Pm10Max]: SensorType.PM,
  [MeasurementType.Pm10Avg]: SensorType.PM,
  [MeasurementType.Pm10Twa]: SensorType.PM,
  [MeasurementType.PmTotalMin]: SensorType.PM,
  [MeasurementType.PmTotalMax]: SensorType.PM,
  [MeasurementType.PmTotalAvg]: SensorType.PM,
  [MeasurementType.PmTotalTwa]: SensorType.PM,
  [MeasurementType.Pm25IndiaAqi]: SensorType.PM,
  [MeasurementType.Pm10IndiaAqi]: SensorType.PM
} as const

export const measurementTypesLabelKey: Record<MeasurementType, string> = {
  [MeasurementType.Pm25]: 'aqi25',
  [MeasurementType.Pm10]: 'aqi10',
  [MeasurementType.Temp]: 'temperature',
  [MeasurementType.Rh]: 'humidity',
  [MeasurementType.Pm25Raw]: 'pm25',
  [MeasurementType.Pm10Raw]: 'pm10',
  [MeasurementType.BaroInhg]: 'pressure',
  [MeasurementType.Co]: 'co',
  [MeasurementType.Co2]: 'co2',
  [MeasurementType.O3]: 'o3',
  [MeasurementType.Tvoc]: 'tvoc',
  [MeasurementType.So2]: 'so2',
  [MeasurementType.No2]: 'no2',
  [MeasurementType.Ch2o]: 'ch2o',
  [MeasurementType.Pm1x0]: 'pm1x0',
  [MeasurementType.Pm4x0]: 'pm4x0',
  [MeasurementType.PmTotal]: 'pm_total',
  [MeasurementType.Pm1x0Min]: 'pm1x0_min',
  [MeasurementType.Pm1x0Max]: 'pm1x0_max',
  [MeasurementType.Pm1x0Avg]: 'pm1x0_avg',
  [MeasurementType.Pm1x0Twa]: 'pm1x0_twa',
  [MeasurementType.Pm2x5Min]: 'pm2x5_min',
  [MeasurementType.Pm2x5Max]: 'pm2x5_max',
  [MeasurementType.Pm2x5Avg]: 'pm2x5_avg',
  [MeasurementType.Pm2x5Twa]: 'pm2x5_twa',
  [MeasurementType.Pm4x0Min]: 'pm4x0_min',
  [MeasurementType.Pm4x0Max]: 'pm4x0_max',
  [MeasurementType.Pm4x0Avg]: 'pm4x0_avg',
  [MeasurementType.Pm4x0Twa]: 'pm4x0_twa',
  [MeasurementType.Pm10Min]: 'pm10_min',
  [MeasurementType.Pm10Max]: 'pm10_max',
  [MeasurementType.Pm10Avg]: 'pm10_avg',
  [MeasurementType.Pm10Twa]: 'pm10_twa',
  [MeasurementType.PmTotalMin]: 'pm_total_min',
  [MeasurementType.PmTotalMax]: 'pm_total_max',
  [MeasurementType.PmTotalAvg]: 'pm_total_avg',
  [MeasurementType.PmTotalTwa]: 'pm_total_twa',
  [MeasurementType.Pm25IndiaAqi]: 'india_aqi25',
  [MeasurementType.Pm10IndiaAqi]: 'india_aqi10'
} as const

export const measurementFriendlyNameKey: Record<MeasurementType, string> = {
  [MeasurementType.Pm25]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.aqi25`,
  [MeasurementType.Pm10]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.aqi10`,
  [MeasurementType.Temp]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.temperature`,
  [MeasurementType.Rh]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.humidity`,
  [MeasurementType.Pm25Raw]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.pm25`,
  [MeasurementType.Pm10Raw]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.pm10`,
  [MeasurementType.BaroInhg]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.pressure`,
  [MeasurementType.Co]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.co`,
  [MeasurementType.Co2]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.co2`,
  [MeasurementType.O3]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.o3`,
  [MeasurementType.Tvoc]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.tvoc`,
  [MeasurementType.So2]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.so2`,
  [MeasurementType.No2]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.no2`,
  [MeasurementType.Ch2o]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.ch2o`,
  [MeasurementType.Pm1x0]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.pm1x0`,
  [MeasurementType.Pm4x0]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.pm4x0`,
  [MeasurementType.PmTotal]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.pm_total`,
  [MeasurementType.Pm1x0Min]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.pm1x0_min`,
  [MeasurementType.Pm1x0Max]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.pm1x0_max`,
  [MeasurementType.Pm1x0Avg]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.pm1x0_avg`,
  [MeasurementType.Pm1x0Twa]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.pm1x0_twa`,
  [MeasurementType.Pm2x5Min]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.pm2x5_min`,
  [MeasurementType.Pm2x5Max]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.pm2x5_max`,
  [MeasurementType.Pm2x5Avg]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.pm2x5_avg`,
  [MeasurementType.Pm2x5Twa]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.pm2x5_twa`,
  [MeasurementType.Pm4x0Min]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.pm4x0_min`,
  [MeasurementType.Pm4x0Max]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.pm4x0_max`,
  [MeasurementType.Pm4x0Avg]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.pm4x0_avg`,
  [MeasurementType.Pm4x0Twa]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.pm4x0_twa`,
  [MeasurementType.Pm10Min]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.pm10_min`,
  [MeasurementType.Pm10Max]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.pm10_max`,
  [MeasurementType.Pm10Avg]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.pm10_avg`,
  [MeasurementType.Pm10Twa]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.pm10_twa`,
  [MeasurementType.PmTotalMin]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.pm_total_min`,
  [MeasurementType.PmTotalMax]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.pm_total_max`,
  [MeasurementType.PmTotalAvg]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.pm_total_avg`,
  [MeasurementType.PmTotalTwa]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.pm_total_twa`,
  [MeasurementType.Pm25IndiaAqi]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.india_aqi25`,
  [MeasurementType.Pm10IndiaAqi]: `${MEASUREMENT_FRIENDLY_NAME_PATH}.india_aqi10`
} as const

export const measurementTypesValueRange: Record<MeasurementType, [number | null, number | null]> = {
  [MeasurementType.Pm25]: [0, null],
  [MeasurementType.Pm10]: [0, null],
  [MeasurementType.Temp]: [null, null],
  [MeasurementType.Rh]: [null, null],
  [MeasurementType.Pm25Raw]: [0, null],
  [MeasurementType.Pm10Raw]: [0, null],
  [MeasurementType.BaroInhg]: [null, null],
  [MeasurementType.Co]: [null, null],
  [MeasurementType.Co2]: [null, null],
  [MeasurementType.O3]: [null, null],
  [MeasurementType.Tvoc]: [null, null],
  [MeasurementType.So2]: [null, null],
  [MeasurementType.No2]: [null, null],
  [MeasurementType.Ch2o]: [null, null],
  [MeasurementType.Pm1x0]: [null, null],
  [MeasurementType.Pm4x0]: [null, null],
  [MeasurementType.PmTotal]: [null, null],
  [MeasurementType.Pm1x0Min]: [null, null],
  [MeasurementType.Pm1x0Max]: [null, null],
  [MeasurementType.Pm1x0Avg]: [null, null],
  [MeasurementType.Pm1x0Twa]: [null, null],
  [MeasurementType.Pm2x5Min]: [null, null],
  [MeasurementType.Pm2x5Max]: [null, null],
  [MeasurementType.Pm2x5Avg]: [null, null],
  [MeasurementType.Pm2x5Twa]: [null, null],
  [MeasurementType.Pm4x0Min]: [null, null],
  [MeasurementType.Pm4x0Max]: [null, null],
  [MeasurementType.Pm4x0Avg]: [null, null],
  [MeasurementType.Pm4x0Twa]: [null, null],
  [MeasurementType.Pm10Min]: [null, null],
  [MeasurementType.Pm10Max]: [null, null],
  [MeasurementType.Pm10Avg]: [null, null],
  [MeasurementType.Pm10Twa]: [null, null],
  [MeasurementType.PmTotalMin]: [null, null],
  [MeasurementType.PmTotalMax]: [null, null],
  [MeasurementType.PmTotalAvg]: [null, null],
  [MeasurementType.PmTotalTwa]: [null, null],
  [MeasurementType.Pm25IndiaAqi]: [0, null],
  [MeasurementType.Pm10IndiaAqi]: [0, null]
} as const

export const measurementTypesUnitsKey: Record<MeasurementType, string | undefined> = {
  [MeasurementType.Pm25]: undefined,
  [MeasurementType.Pm10]: undefined,
  [MeasurementType.Temp]: undefined,
  [MeasurementType.Rh]: 'units.percent',
  [MeasurementType.Pm25Raw]: undefined,
  [MeasurementType.Pm10Raw]: undefined,
  [MeasurementType.BaroInhg]: 'units.inchMercury',
  [MeasurementType.Co]: 'units.partsPerMillion',
  [MeasurementType.Co2]: 'units.partsPerMillion',
  [MeasurementType.O3]: 'units.partsPerBillion',
  [MeasurementType.Tvoc]: 'units.mgm3',
  [MeasurementType.So2]: 'units.partsPerBillion',
  [MeasurementType.No2]: 'units.partsPerBillion',
  [MeasurementType.Ch2o]: 'units.partsPerBillion',
  [MeasurementType.Pm1x0]: undefined,
  [MeasurementType.Pm4x0]: undefined,
  [MeasurementType.PmTotal]: undefined,
  [MeasurementType.Pm1x0Min]: undefined,
  [MeasurementType.Pm1x0Max]: undefined,
  [MeasurementType.Pm1x0Avg]: undefined,
  [MeasurementType.Pm1x0Twa]: undefined,
  [MeasurementType.Pm2x5Min]: undefined,
  [MeasurementType.Pm2x5Max]: undefined,
  [MeasurementType.Pm2x5Avg]: undefined,
  [MeasurementType.Pm2x5Twa]: undefined,
  [MeasurementType.Pm4x0Min]: undefined,
  [MeasurementType.Pm4x0Max]: undefined,
  [MeasurementType.Pm4x0Avg]: undefined,
  [MeasurementType.Pm4x0Twa]: undefined,
  [MeasurementType.Pm10Min]: undefined,
  [MeasurementType.Pm10Max]: undefined,
  [MeasurementType.Pm10Avg]: undefined,
  [MeasurementType.Pm10Twa]: undefined,
  [MeasurementType.PmTotalMin]: undefined,
  [MeasurementType.PmTotalMax]: undefined,
  [MeasurementType.PmTotalAvg]: undefined,
  [MeasurementType.PmTotalTwa]: undefined,
  [MeasurementType.Pm25IndiaAqi]: undefined,
  [MeasurementType.Pm10IndiaAqi]: undefined
} as const

export const measurementImgSrc: Record<MeasurementType, string> = {
  [MeasurementType.Pm25]: MEASUREMENT_IMG_SRC.PM,
  [MeasurementType.Pm10]: MEASUREMENT_IMG_SRC.PM,
  [MeasurementType.Temp]: MEASUREMENT_IMG_SRC.TEMP,
  [MeasurementType.Rh]: MEASUREMENT_IMG_SRC.RH,
  [MeasurementType.Pm25Raw]: MEASUREMENT_IMG_SRC.PM,
  [MeasurementType.Pm10Raw]: MEASUREMENT_IMG_SRC.PM,
  [MeasurementType.BaroInhg]: MEASUREMENT_IMG_SRC.BP,
  [MeasurementType.Co]: MEASUREMENT_IMG_SRC.CO,
  [MeasurementType.Co2]: MEASUREMENT_IMG_SRC.CO2,
  [MeasurementType.O3]: MEASUREMENT_IMG_SRC.O3,
  [MeasurementType.Tvoc]: MEASUREMENT_IMG_SRC.VOC,
  [MeasurementType.So2]: MEASUREMENT_IMG_SRC.SO2,
  [MeasurementType.No2]: MEASUREMENT_IMG_SRC.NO2,
  [MeasurementType.Ch2o]: MEASUREMENT_IMG_SRC.CH2O,
  [MeasurementType.Pm1x0]: MEASUREMENT_IMG_SRC.PM,
  [MeasurementType.Pm4x0]: MEASUREMENT_IMG_SRC.PM,
  [MeasurementType.PmTotal]: MEASUREMENT_IMG_SRC.PM,
  [MeasurementType.Pm1x0Min]: MEASUREMENT_IMG_SRC.MINIMUM,
  [MeasurementType.Pm1x0Max]: MEASUREMENT_IMG_SRC.MAXIMUM,
  [MeasurementType.Pm1x0Avg]: MEASUREMENT_IMG_SRC.AVERAGE,
  [MeasurementType.Pm1x0Twa]: MEASUREMENT_IMG_SRC.TWA,
  [MeasurementType.Pm2x5Min]: MEASUREMENT_IMG_SRC.MINIMUM,
  [MeasurementType.Pm2x5Max]: MEASUREMENT_IMG_SRC.MAXIMUM,
  [MeasurementType.Pm2x5Avg]: MEASUREMENT_IMG_SRC.AVERAGE,
  [MeasurementType.Pm2x5Twa]: MEASUREMENT_IMG_SRC.TWA,
  [MeasurementType.Pm4x0Min]: MEASUREMENT_IMG_SRC.MINIMUM,
  [MeasurementType.Pm4x0Max]: MEASUREMENT_IMG_SRC.MAXIMUM,
  [MeasurementType.Pm4x0Avg]: MEASUREMENT_IMG_SRC.AVERAGE,
  [MeasurementType.Pm4x0Twa]: MEASUREMENT_IMG_SRC.TWA,
  [MeasurementType.Pm10Min]: MEASUREMENT_IMG_SRC.MINIMUM,
  [MeasurementType.Pm10Max]: MEASUREMENT_IMG_SRC.MAXIMUM,
  [MeasurementType.Pm10Avg]: MEASUREMENT_IMG_SRC.AVERAGE,
  [MeasurementType.Pm10Twa]: MEASUREMENT_IMG_SRC.TWA,
  [MeasurementType.PmTotalMin]: MEASUREMENT_IMG_SRC.MINIMUM,
  [MeasurementType.PmTotalMax]: MEASUREMENT_IMG_SRC.MAXIMUM,
  [MeasurementType.PmTotalAvg]: MEASUREMENT_IMG_SRC.AVERAGE,
  [MeasurementType.PmTotalTwa]: MEASUREMENT_IMG_SRC.TWA,
  [MeasurementType.Pm25IndiaAqi]: MEASUREMENT_IMG_SRC.PM,
  [MeasurementType.Pm10IndiaAqi]: MEASUREMENT_IMG_SRC.PM
} as const

export const measurementTypesOrder: MeasurementType[] = [
  MeasurementType.Pm1x0,
  MeasurementType.Pm25,
  MeasurementType.Pm25Raw,
  MeasurementType.Pm4x0,
  MeasurementType.Pm10,
  MeasurementType.Pm10Raw,
  MeasurementType.PmTotal,
  MeasurementType.Pm1x0Min,
  MeasurementType.Pm1x0Max,
  MeasurementType.Pm1x0Avg,
  MeasurementType.Pm1x0Twa,
  MeasurementType.Pm2x5Min,
  MeasurementType.Pm2x5Max,
  MeasurementType.Pm2x5Avg,
  MeasurementType.Pm2x5Twa,
  MeasurementType.Pm4x0Min,
  MeasurementType.Pm4x0Max,
  MeasurementType.Pm4x0Avg,
  MeasurementType.Pm4x0Twa,
  MeasurementType.Pm10Min,
  MeasurementType.Pm10Max,
  MeasurementType.Pm10Avg,
  MeasurementType.Pm10Twa,
  MeasurementType.PmTotalMin,
  MeasurementType.PmTotalMax,
  MeasurementType.PmTotalAvg,
  MeasurementType.PmTotalTwa,
  MeasurementType.Rh,
  MeasurementType.Temp,
  MeasurementType.BaroInhg,
  MeasurementType.Co,
  MeasurementType.Co2,
  MeasurementType.O3,
  MeasurementType.Tvoc,
  MeasurementType.So2,
  MeasurementType.No2,
  MeasurementType.Ch2o
]

export const allMeasurementStatistics: MeasurementType[] = [
  MeasurementType.Pm1x0Min,
  MeasurementType.Pm1x0Max,
  MeasurementType.Pm1x0Avg,
  MeasurementType.Pm1x0Twa,
  MeasurementType.Pm2x5Min,
  MeasurementType.Pm2x5Max,
  MeasurementType.Pm2x5Avg,
  MeasurementType.Pm2x5Twa,
  MeasurementType.Pm4x0Min,
  MeasurementType.Pm4x0Max,
  MeasurementType.Pm4x0Avg,
  MeasurementType.Pm4x0Twa,
  MeasurementType.Pm10Min,
  MeasurementType.Pm10Max,
  MeasurementType.Pm10Avg,
  MeasurementType.Pm10Twa,
  MeasurementType.PmTotalMin,
  MeasurementType.PmTotalMax,
  MeasurementType.PmTotalAvg,
  MeasurementType.PmTotalTwa
]

export interface MeasurementStatistics {
  minimum: MeasurementType
  maximum: MeasurementType
  average: MeasurementType
  twa: MeasurementType
}

export const measurementStatistics: Record<MeasurementType, MeasurementStatistics | null> = {
  [MeasurementType.Pm25]: null,
  [MeasurementType.Pm10]: null,
  [MeasurementType.Temp]: null,
  [MeasurementType.Rh]: null,
  [MeasurementType.Pm25Raw]: {
    minimum: MeasurementType.Pm2x5Min,
    maximum: MeasurementType.Pm2x5Max,
    average: MeasurementType.Pm2x5Avg,
    twa: MeasurementType.Pm2x5Twa
  },
  [MeasurementType.Pm10Raw]: {
    minimum: MeasurementType.Pm10Min,
    maximum: MeasurementType.Pm10Max,
    average: MeasurementType.Pm10Avg,
    twa: MeasurementType.Pm10Twa
  },
  [MeasurementType.BaroInhg]: null,
  [MeasurementType.Co]: null,
  [MeasurementType.Co2]: null,
  [MeasurementType.O3]: null,
  [MeasurementType.Tvoc]: null,
  [MeasurementType.So2]: null,
  [MeasurementType.No2]: null,
  [MeasurementType.Ch2o]: null,
  [MeasurementType.Pm1x0]: {
    minimum: MeasurementType.Pm1x0Min,
    maximum: MeasurementType.Pm1x0Max,
    average: MeasurementType.Pm1x0Avg,
    twa: MeasurementType.Pm1x0Twa
  },
  [MeasurementType.Pm4x0]: {
    minimum: MeasurementType.Pm4x0Min,
    maximum: MeasurementType.Pm4x0Max,
    average: MeasurementType.Pm4x0Avg,
    twa: MeasurementType.Pm4x0Twa
  },
  [MeasurementType.PmTotal]: {
    minimum: MeasurementType.PmTotalMin,
    maximum: MeasurementType.PmTotalMax,
    average: MeasurementType.PmTotalAvg,
    twa: MeasurementType.PmTotalTwa
  },
  [MeasurementType.Pm1x0Min]: null,
  [MeasurementType.Pm1x0Max]: null,
  [MeasurementType.Pm1x0Avg]: null,
  [MeasurementType.Pm1x0Twa]: null,
  [MeasurementType.Pm2x5Min]: null,
  [MeasurementType.Pm2x5Max]: null,
  [MeasurementType.Pm2x5Avg]: null,
  [MeasurementType.Pm2x5Twa]: null,
  [MeasurementType.Pm4x0Min]: null,
  [MeasurementType.Pm4x0Max]: null,
  [MeasurementType.Pm4x0Avg]: null,
  [MeasurementType.Pm4x0Twa]: null,
  [MeasurementType.Pm10Min]: null,
  [MeasurementType.Pm10Max]: null,
  [MeasurementType.Pm10Avg]: null,
  [MeasurementType.Pm10Twa]: null,
  [MeasurementType.PmTotalMin]: null,
  [MeasurementType.PmTotalMax]: null,
  [MeasurementType.PmTotalAvg]: null,
  [MeasurementType.PmTotalTwa]: null,
  [MeasurementType.Pm25IndiaAqi]: null,
  [MeasurementType.Pm10IndiaAqi]: null
} as const

export const getMeasurementTypeKeyByName = (measurementName: string): MeasurementType => {
  return Number(
    Object.keys(measurementTypesKey).find((key) => {
      return measurementTypesKey[Number(key) as MeasurementType] === measurementName
    })
  )
}
